/**
 * @file: factory.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { config } from '@euler/config';
import { HttpClient } from '@euler/lib/request';
import {
  CacheService,
  StorageService,
  TokenService,
} from '@euler/lib/services';
import { AsyncStorageProvider } from '@euler/lib/storage/impl/AsyncStorageProvider';
import { SecureStorageProvider } from '@euler/lib/storage/impl/SecureStorageProvider';
import {
  AIAssistantService,
  AiAttachmentService,
  AiCreditService,
  ApdmCampaignService,
  ApdmCouponService,
  ApdmReviewService,
  ApdmService,
  AppDistService,
  AuthService,
  BiService,
  ChatService,
  CoachingService,
  FeedService,
  InventoryService,
  LeadService,
  MaintenancePlanService,
  MediaFileService,
  OcrService,
  OrderService,
  OrganizationService,
  PosterStudioService,
  ProductService,
  ReportService,
  ReservationService,
  ServiceRecordService,
  SmsService,
  StoreErpIntegrationService,
  StoreService,
  SupportService,
  SystemService,
  TaskService,
  UserService,
  VehicleInfoService,
  VoiceService,
} from '@euler/services';
import {
  AuthTokenMiddleware,
  AuthenticationInvalidationMiddleware,
  ClientInfoMiddleware,
  ClientPlatformRefusalMiddleware,
  MaintenanceMiddleware,
  TokenRefreshMiddleware,
} from '@euler/services/middlewares';
import { makeDebug } from '@euler/utils';
import React, { ReactNode, useContext } from 'react';
import { Platform } from 'react-native';

const debug = makeDebug('auth:token-refresh:factory', __DEV__);

export const defaultStorageService = new StorageService(
  AsyncStorageProvider.shared,
);
export const defaultSecureStorageService = new StorageService(
  SecureStorageProvider.shared,
);
export const cacheService = new CacheService(defaultStorageService);

export const defaultTokenService = new TokenService(
  Platform.select({
    web: defaultStorageService,
    native: defaultSecureStorageService,
  })!,
);

const refreshApi = new HttpClient(cacheService, {
  endPoint: config.apiEndpoint,
  timeout: config.requestTimeout,
  prefix: { url: '/api' },
  middlewares: [new ClientInfoMiddleware()],
});
export const refreshAuthService = new AuthService(refreshApi);

/**
 * Single source-of-truth for "swap stored refresh-token for a fresh
 * access-token". Used by the AI service's SSE-on-401 handler and the
 * chat WS's pre-connect freshness check (so a near-expiry JWT doesn't
 * cause every reconnect to be auth-rejected, the symptom of the long-
 * stall reconnect bug).
 */
export async function refreshAccessToken(): Promise<string | null> {
  const startedAt = Date.now();
  const rt = await defaultTokenService.getRefreshToken();
  if (!rt) {
    debug('refreshAccessToken skipped: no refresh token');
    return null;
  }
  try {
    debug('refreshAccessToken calling refresh endpoint');
    const result = await refreshAuthService.refresh(rt);
    await defaultTokenService.setToken(result.token);
    await defaultTokenService.setRefreshToken(result.refreshToken);
    await defaultTokenService.setRefreshTokenExpiry(
      Date.now() + result.refreshTokenExpiresIn * 1000,
    );
    debug(
      'refreshAccessToken succeeded in %dms (next refresh expiresIn: %ds)',
      Date.now() - startedAt,
      result.refreshTokenExpiresIn,
    );
    return result.token;
  } catch (e) {
    debug(
      'refreshAccessToken failed after %dms: %o',
      Date.now() - startedAt,
      e,
    );
    return null;
  }
}

const systemApi = new HttpClient(cacheService, {
  endPoint: config.apiEndpoint,
  timeout: config.requestTimeout,
  prefix: { url: '/api' },
  middlewares: [new ClientInfoMiddleware()],
});
export const systemService = new SystemService(systemApi);

export const api = new HttpClient(cacheService, {
  endPoint: config.apiEndpoint,
  timeout: config.requestTimeout,
  prefix: { url: '/api' },
  middlewares: [
    new AuthTokenMiddleware(),
    new ClientInfoMiddleware(),
    new TokenRefreshMiddleware(
      () => defaultTokenService.getRefreshToken(),
      rt => refreshAuthService.refresh(rt),
      async result => {
        await defaultTokenService.setToken(result.token);
        await defaultTokenService.setRefreshToken(result.refreshToken);
        await defaultTokenService.setRefreshTokenExpiry(
          Date.now() + result.refreshTokenExpiresIn * 1000,
        );
      },
      () => defaultTokenService.getRefreshTokenExpiry(),
      () => defaultTokenService.getToken(),
    ),
    new MaintenanceMiddleware(),
    new ClientPlatformRefusalMiddleware(),
    new AuthenticationInvalidationMiddleware(),
  ],
});

const aiApi = new HttpClient(cacheService, {
  endPoint: config.aiEndpoint,
  timeout: 120000,
  prefix: { url: '/api/ai' },
  middlewares: [
    new AuthTokenMiddleware(),
    new ClientInfoMiddleware(),
    new TokenRefreshMiddleware(
      () => defaultTokenService.getRefreshToken(),
      rt => refreshAuthService.refresh(rt),
      async result => {
        await defaultTokenService.setToken(result.token);
        await defaultTokenService.setRefreshToken(result.refreshToken);
        await defaultTokenService.setRefreshTokenExpiry(
          Date.now() + result.refreshTokenExpiresIn * 1000,
        );
      },
      () => defaultTokenService.getRefreshTokenExpiry(),
      () => defaultTokenService.getToken(),
    ),
    new AuthenticationInvalidationMiddleware(),
  ],
});

export const serviceFactory = {
  defaultStorageService,
  defaultSecureStorageService,
  defaultTokenService,
  cacheService,
  apdmService: new ApdmService(api, aiApi, {
    endpoint: config.aiEndpoint,
    tokenService: defaultTokenService,
    refreshToken: refreshAccessToken,
  }),
  apdmCampaignService: new ApdmCampaignService(api),
  apdmCouponService: new ApdmCouponService(api),
  apdmReviewService: new ApdmReviewService(api),
  aiAttachmentService: new AiAttachmentService(api),
  aiAssistantService: new AIAssistantService(
    aiApi,
    defaultTokenService,
    config.aiEndpoint,
    refreshAccessToken,
  ),
  coachingService: new CoachingService(
    api,
    aiApi,
    defaultTokenService,
    config.aiEndpoint,
    refreshAccessToken,
  ),
  aiCreditService: new AiCreditService(api),
  posterStudioService: new PosterStudioService(api),
  authService: new AuthService(api),
  biService: new BiService(api),
  userService: new UserService(api),
  smsService: new SmsService(api),
  taskService: new TaskService(api),
  storeService: new StoreService(api),
  storeErpIntegrationService: new StoreErpIntegrationService(api),
  ocrService: new OcrService(api),
  orderService: new OrderService(api),
  vehicleInfoService: new VehicleInfoService(api),
  inventoryService: new InventoryService(api),
  mediaFileService: new MediaFileService(api),
  supportService: new SupportService(api),
  appDistService: new AppDistService(api),
  reportService: new ReportService(api),
  serviceRecordService: new ServiceRecordService(api),
  organizationService: new OrganizationService(api),
  productService: new ProductService(api),
  maintenancePlanService: new MaintenancePlanService(api),
  voiceService: new VoiceService(api),
  chatService: new ChatService(api),
  feedService: new FeedService(api),
  leadService: new LeadService(api),
  reservationService: new ReservationService(api),
};

export type ServiceFactory = typeof serviceFactory;

const ServiceFactoryContext = React.createContext<typeof serviceFactory>(
  null as any,
);

export const useServiceFactory = () => useContext(ServiceFactoryContext);

export const ServiceFactoryProvider = (props: {
  serviceFactory?: typeof serviceFactory;
  children: ReactNode;
}) => {
  return (
    <ServiceFactoryContext.Provider
      value={props.serviceFactory ?? serviceFactory}
    >
      {props.children}
    </ServiceFactoryContext.Provider>
  );
};
